/**
 * QUESTIONS (EXPERT-ROOMS.md §13) — the consult desk.
 *
 * In an expert team's room: the questions WAITING on this team (a department: its own; the Lead: every team's), each
 * with the asker, the team's draft + how sure it was, who is answering it and when it escalates → Claim · Send draft ·
 * Answer (the person's own words, posted AS WRITTEN) · Not ours → another team. Then the team's settings (owner, SLA,
 * the bar it must clear to answer alone, its track record) and its TRAINED answers (edit · retire · promote to a rule
 * the network's builders accept). In any room: what it asked the experts, with Cancel while open.
 * The server decides who may act; a refusal says why. Additive section.
 */
import React, { useState } from 'react';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const TONE = { routing: 'bg-gray-100 text-gray-700', waiting: 'bg-amber-100 text-amber-900',
  escalated: 'bg-rose-100 text-rose-900', answered: 'bg-emerald-100 text-emerald-900', cancelled: 'bg-gray-100 text-gray-600' };
const pct = (c) => (typeof c === 'number' ? `${Math.round(c * 100)}%` : '—');

function useRun(onAct) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async (req) => {
    setBusy(true); setErr('');
    try { return (await onAct(req)) || true; } catch (e) { setErr(e?.message || "Couldn't do that."); return false; } finally { setBusy(false); }
  };
  return { busy, err, run };
}

const KIND_LABEL = { process: 'A reusable process', policy: 'A rule', fact: "This company's fact (stays here)",
  specific: 'Specific to this case' };

/** MAKE IT REUSABLE (EXPERT-ROOMS.md §13a): the distiller's candidate — only reusable process / policy (published with
 *  the expert) or the company's own fact (never published); identifiers already replaced; the person edits, then
 *  suggests it. Not promotable → why, and it stays a trained answer. */
function Reusable({ cand, responsibilities, busy, run, onClose, base }) {
  const p = cand.procedure || {};
  const [title, setTitle] = useState(p.title || '');
  const [text, setText] = useState(p.text || '');
  const [statement, setStatement] = useState(cand.rule?.statement || '');
  const [severity, setSeverity] = useState(cand.rule?.severity || 'warn');
  const [respKey, setRespKey] = useState('');
  const submit = (req) => run({ ...base, action: 'promote', ...req }).then((ok) => { if (ok) onClose(); });
  return (
    <div className="mt-1 space-y-1 rounded bg-indigo-50/40 p-1.5" data-testid={`trained-reusable-${base.trainedId}`}>
      <p className="text-[11px] font-medium text-gray-800">{KIND_LABEL[cand.kind] || cand.kind}</p>
      {cand.why ? <p className="text-[10px] text-gray-600">{cand.why}</p> : null}
      {(cand.replaced || []).length ? <p className="text-[10px] text-gray-500">Replaced with placeholders: {cand.replaced.join(', ')}</p> : null}
      {(cand.removed || []).length ? <p className="text-[10px] text-gray-500">Left out: {cand.removed.join('; ')}</p> : null}
      {!cand.promotable ? (
        <div className="flex justify-end"><button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={onClose}>OK — keep it as a trained answer</button></div>
      ) : cand.kind === 'process' ? (
        <>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="block w-full rounded border border-gray-300 px-2 py-0.5 text-[11px]" />
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid={`trained-reusable-text-${base.trainedId}`}
            className="block w-full rounded border border-gray-300 px-2 py-0.5 text-[11px]" />
          <p className="text-[10px] text-gray-500">{p.replaces ? `Updates the procedure "${p.id}".` : `A new procedure "${p.id}".`} The network's builders add it on the Rules tab; it ships with the next version.</p>
          <div className="flex justify-end gap-2">
            <button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={onClose}>Cancel</button>
            <button type="button" disabled={busy || !text.trim()} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
              data-testid={`trained-reusable-send-${base.trainedId}`}
              onClick={() => submit({ kind: 'process', procedureId: p.id, title, text })}>Suggest this procedure</button>
          </div>
        </>
      ) : cand.kind === 'policy' ? (
        <>
          <select value={respKey} onChange={(e) => setRespKey(e.target.value)} className="block w-full rounded border border-gray-300 px-1 py-0.5 text-[11px]">
            <option value="">Which responsibility does it belong to?</option>
            {responsibilities.map((r) => <option key={r.key} value={r.key}>{r.team} · {r.title}</option>)}
          </select>
          <input value={statement} onChange={(e) => setStatement(e.target.value)} className="block w-full rounded border border-gray-300 px-2 py-0.5 text-[11px]" />
          <div className="flex items-center justify-end gap-2">
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="rounded border border-gray-300 px-1 py-0.5 text-[11px]">
              <option value="block">block</option><option value="warn">warn</option><option value="advise">advise</option>
            </select>
            <button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={onClose}>Cancel</button>
            <button type="button" disabled={busy || !respKey || !statement.trim()} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
              onClick={() => submit({ kind: 'rule', respKey, statement, severity })}>Suggest this rule</button>
          </div>
        </>
      ) : (
        <>
          <pre className="whitespace-pre-wrap rounded bg-white px-2 py-1 text-[10px] text-gray-700">{cand.fact?.kind}: {JSON.stringify(cand.fact?.record || {}, null, 1)}</pre>
          <div className="flex justify-end gap-2">
            <button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={onClose}>Cancel</button>
            <button type="button" disabled={busy} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}
              onClick={() => submit({ kind: 'fact', factKind: cand.fact?.kind, record: cand.fact?.record })}>Keep as this company&apos;s fact</button>
          </div>
        </>
      )}
    </div>
  );
}

function Waiting({ c, teams, canAnswer, onAct }) {
  const { busy, err, run } = useRun(onAct);
  const [answering, setAnswering] = useState(false);
  const [text, setText] = useState(c.draft || '');
  const [to, setTo] = useState('');
  const base = { expertNetworkId: c.expertNetworkId, consultId: c.consultId };
  return (
    <div className="rounded-md border border-gray-200 px-2 py-1.5" data-testid={`consult-${c.consultId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${TONE[c.status] || ''}`}>{c.status}</span>
        <span className="text-[10px] text-gray-500">from {c.asker?.name || 'a room'} · {c.team} team</span>
        {c.claimedByName ? <span className="text-[10px] text-indigo-700">{c.claimedByName} is answering</span> : null}
        {c.dueAt ? <span className="text-[10px] text-gray-400" title="When the Lead is told">escalates {new Date(c.dueAt).toLocaleString()}</span> : null}
      </div>
      <p className="mt-1 text-[12px] font-medium text-gray-900">{c.question}</p>
      {c.draft ? (
        <div className="mt-1 rounded bg-gray-50 px-2 py-1" data-testid={`consult-draft-${c.consultId}`}>
          <p className="text-[10px] text-gray-500">The team&apos;s draft · {pct(c.confidence)} sure{c.basis ? ` — ${c.basis}` : ''}</p>
          <p className="whitespace-pre-wrap text-[11px] text-gray-800">{c.draft}</p>
        </div>
      ) : <p className="mt-1 text-[11px] text-gray-500">No draft — {c.basis || 'write the answer'}.</p>}
      {canAnswer ? (
        answering ? (
          <div className="mt-1.5 space-y-1">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} data-testid={`consult-answer-text-${c.consultId}`}
              className="block w-full rounded-md border border-gray-300 px-2 py-1 text-[12px]"
              placeholder="Your answer — posted exactly as you write it, and kept for next time" />
            <div className="flex justify-end gap-2">
              <button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={() => setAnswering(false)}>Cancel</button>
              <button type="button" disabled={busy || !text.trim()} data-testid={`consult-answer-send-${c.consultId}`}
                className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
                onClick={async () => { if (await run({ ...base, action: 'answer', text })) setAnswering(false); }}>Send answer</button>
            </div>
          </div>
        ) : (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {!c.claimedByName ? (
              <button type="button" disabled={busy} className={`${btn} text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-50`}
                data-testid={`consult-claim-${c.consultId}`} onClick={() => run({ ...base, action: 'claim' })}>I&apos;ll answer</button>
            ) : null}
            {c.draft ? (
              <button type="button" disabled={busy} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
                data-testid={`consult-send-${c.consultId}`} onClick={() => run({ ...base, action: 'send' })}>Send draft</button>
            ) : null}
            <button type="button" disabled={busy} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}
              data-testid={`consult-answer-${c.consultId}`} onClick={() => { setText(c.draft || ''); setAnswering(true); }}>
              {c.draft ? 'Edit & send' : 'Answer'}</button>
            {teams.length > 1 ? (
              <span className="flex items-center gap-1">
                <select value={to} onChange={(e) => setTo(e.target.value)} className="rounded border border-gray-200 px-1 py-0.5 text-[11px]"
                  data-testid={`consult-reassign-to-${c.consultId}`}>
                  <option value="">Not ours →</option>
                  {teams.filter((t) => t.toLowerCase() !== String(c.team).toLowerCase()).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {to ? <button type="button" disabled={busy} className={`${btn} text-gray-700 hover:bg-gray-100`}
                  data-testid={`consult-reassign-${c.consultId}`} onClick={() => run({ ...base, action: 'reassign', team: to })}>Move</button> : null}
              </span>
            ) : null}
          </div>
        )
      ) : null}
      {err ? <p className="mt-1 text-[11px] text-rose-600">{err}</p> : null}
    </div>
  );
}

function Trained({ t, responsibilities, canAnswer, onAct }) {
  const { busy, err, run } = useRun(onAct);
  const [edit, setEdit] = useState(false);
  const [q, setQ] = useState(t.question);
  const [a, setA] = useState(t.answer);
  const [cand, setCand] = useState(null);
  const base = { team: t.team, trainedId: t.trainedId };
  return (
    <div className="rounded-md border border-gray-100 px-2 py-1.5" data-testid={`trained-${t.trainedId}`}>
      {edit ? (
        <div className="space-y-1">
          <input value={q} onChange={(e) => setQ(e.target.value)} className="block w-full rounded border border-gray-300 px-2 py-0.5 text-[11px]" />
          <textarea value={a} onChange={(e) => setA(e.target.value)} rows={4} className="block w-full rounded border border-gray-300 px-2 py-0.5 text-[11px]" />
          <div className="flex justify-end gap-2">
            <button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={() => setEdit(false)}>Cancel</button>
            <button type="button" disabled={busy} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}
              onClick={async () => { if (await run({ ...base, action: 'trained', question: q, answer: a })) setEdit(false); }}>Save</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-[11px] font-medium text-gray-900">Q: {t.question}</p>
          <p className="whitespace-pre-wrap text-[11px] text-gray-700">A: {t.answer}</p>
          <p className="mt-0.5 text-[10px] text-gray-400">
            {t.team} · taught by {t.by || 'a teammate'} · used {t.uses}×
            {t.promotedTo?.kind ? ` · suggested as a ${t.promotedTo.kind === 'fact' ? "company fact" : t.promotedTo.kind}` : ''}
          </p>
        </>
      )}
      {canAnswer && !edit ? (
        <div className="mt-1 flex flex-wrap gap-2">
          <button type="button" className={`${btn} text-gray-600 hover:bg-gray-100`} onClick={() => setEdit(true)}>Edit</button>
          <button type="button" disabled={busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
            data-testid={`trained-retire-${t.trainedId}`} onClick={() => run({ ...base, action: 'trained', status: 'retired' })}>Retire</button>
          {!t.promotedTo?.kind ? (
            <button type="button" disabled={busy} className={`${btn} ${t.suggestPromote ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'text-indigo-700 hover:bg-indigo-50'}`}
              data-testid={`trained-promote-${t.trainedId}`}
              title="Keep only what any team using this expertise could reuse — never this company's data"
              onClick={async () => { const r = await run({ ...base, action: 'distill' }); if (r && r.candidate) setCand(r.candidate); }}>
              {t.suggestPromote ? `Used ${t.uses}× — make it reusable` : 'Make it reusable…'}</button>
          ) : null}
        </div>
      ) : null}
      {cand ? <Reusable cand={cand} responsibilities={responsibilities} busy={busy} run={run} base={base}
        onClose={() => setCand(null)} /> : null}
      {err ? <p className="mt-1 text-[11px] text-rose-600">{err}</p> : null}
    </div>
  );
}

function TeamSettings({ team, canAnswer, onAct, me }) {
  const { busy, err, run } = useRun(onAct);
  const [sla, setSla] = useState(String(team.slaHours || 4));
  const answered = (team.sent || 0) + (team.edited || 0);
  return (
    <div className="rounded-md bg-gray-50 px-2 py-1.5 text-[11px] text-gray-700" data-testid="consult-team">
      <p>
        <span className="font-medium">{team.name} team</span> · answers on its own once {team.reviewed}/5 reviewed and
        {' '}≥{Math.round((team.threshold || 0.8) * 100)}% sure · {answered} answered by people
        ({team.sent || 0} drafts sent as is) · {team.auto || 0} on its own
      </p>
      <p className="mt-0.5">Owner: {team.ownerName || 'none — every builder is told'} · escalates after {team.slaHours}h (business hours)</p>
      {canAnswer ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {me && team.ownerSub !== me.sub ? (
            <button type="button" disabled={busy} className={`${btn} text-gray-700 ring-1 ring-gray-200 hover:bg-white`}
              onClick={() => run({ action: 'team', team: team.name, ownerSub: me.sub, ownerName: me.name })}>Make me the owner</button>
          ) : null}
          <label className="flex items-center gap-1">SLA
            <input value={sla} onChange={(e) => setSla(e.target.value)} className="w-12 rounded border border-gray-300 px-1 py-0.5" /> h
          </label>
          <button type="button" disabled={busy} className={`${btn} text-gray-700 hover:bg-white`}
            onClick={() => run({ action: 'team', team: team.name, slaHours: Number(sla) || 4 })}>Save</button>
        </div>
      ) : null}
      {err ? <p className="mt-1 text-rose-600">{err}</p> : null}
    </div>
  );
}

function Asked({ c, onAct }) {
  const { busy, err, run } = useRun(onAct);
  const open = ['routing', 'waiting', 'escalated'].includes(c.status);
  return (
    <div className="flex items-start gap-2 py-0.5" data-testid={`asked-${c.consultId}`}>
      <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${TONE[c.status] || ''}`}>{c.status}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] text-gray-800">{c.question}</p>
        <p className="text-[10px] text-gray-500">{c.expertName}{c.team ? ` · ${c.team} team` : ''}{c.answer?.byName ? ` · by ${c.answer.byName}` : ''}</p>
      </div>
      {open ? <button type="button" disabled={busy} className={`${btn} text-gray-600 hover:bg-gray-100`}
        onClick={() => run({ action: 'cancel', expertNetworkId: c.expertNetworkId, consultId: c.consultId })}>Cancel</button> : null}
      {err ? <span className="text-[10px] text-rose-600">{err}</span> : null}
    </div>
  );
}

export default function RoomConsults({ value, onAct, me = null }) {
  if (!value) return null;
  const queue = Array.isArray(value.queue) ? value.queue.filter((c) => ['waiting', 'escalated'].includes(c.status)) : null;
  const trained = value.trained || [];
  return (
    <div className="space-y-3" data-testid="room-consults">
      {value.locked ? (
        <p className="rounded-md bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800" data-testid="consults-released">
          {value.stage === 'prod' ? 'Released (Production)' : 'Released (QA)'} — this team answers from its release. What
          you answer here goes to the Training team and reaches this room with the next release.
        </p>
      ) : null}
      {queue ? (
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium text-gray-800">Waiting for {value.queueScope === 'all teams' ? 'your teams' : `the ${value.queueScope} team`} ({queue.length})</p>
          {queue.length ? queue.map((c) => (
            <Waiting key={c.consultId} c={c} teams={value.teams || []} canAnswer={!!value.canAnswer} onAct={onAct} />
          )) : <p className="text-[11px] text-gray-400">Nothing waiting.</p>}
        </div>
      ) : null}
      {value.team ? <TeamSettings team={value.team} canAnswer={!!value.canAnswer} onAct={onAct} me={me} /> : null}
      {queue ? (
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium text-gray-800">What the team has taught ({trained.length})</p>
          {trained.length ? trained.map((t) => (
            <Trained key={t.trainedId} t={t} responsibilities={value.responsibilities || []}
              canAnswer={!!value.canAnswer && !value.locked} onAct={onAct} />
          )) : <p className="text-[11px] text-gray-400">Every answer a person sends is kept here and reused for the same question.</p>}
        </div>
      ) : null}
      {(value.outgoing || []).length ? (
        <div>
          <p className="text-[11px] font-medium text-gray-800">This room asked</p>
          {value.outgoing.map((c) => <Asked key={c.consultId} c={c} onAct={onAct} />)}
        </div>
      ) : null}
    </div>
  );
}
